import React, {useEffect, useState, useCallback, useRef} from "react"
import axios from "axios"
import {SERVER_URI} from "../utility/config"

export const ReservedShifts = (props) => {
	const [currentDate, setCurrentDate] = useState(new Date())
	const currentMonth = currentDate.getMonth()
	const currentYear = currentDate.getFullYear()
	const [monthlyShifts, setMonthlyShifts] = useState([]) // This should be fetched from the server
	const [isLoaded, setIsLoaded] = useState(false)
	const [selectedCells, setSelectedCells] = useState(new Set())
	const [isSelecting, setIsSelecting] = useState(false)
	const [selectionStart, setSelectionStart] = useState(null)
	const [copiedCells, setCopiedCells] = useState(new Set())
	const [copiedData, setCopiedData] = useState({})
	const driverList = props.driverList || []
	const [isAddingShift, setIsAddingShift] = useState(false)
	const [selectedDriver, setSelectedDriver] = useState("")
	const [isModifyingShift, setIsModifyingShift] = useState(false)
	const [startTime, setStartTime] = useState("")
	const [endTime, setEndTime] = useState("")
	const [skippedCells, setSkippedCells] = useState([])
	const [pasteWarning, setPasteWarning] = useState("");

	const handleModifyShift = () => {
		if (selectedCells.size === 0) return

		// Get existing data and populate times if available
		const selectedData = getSelectedData()
		const firstShift = Object.values(selectedData)[0]
		if (firstShift) {
			const firstVehicle = Object.values(firstShift)[0]
			if (firstVehicle) {
				const firstShiftData = Object.values(firstVehicle)[0]
				if (firstShiftData?.startTime && firstShiftData?.endTime) {
					// Convert ISO datetime to time format (HH:MM)
					const startDate = new Date(firstShiftData.startTime)
					const endDate = new Date(firstShiftData.endTime)
					setStartTime(startDate.toTimeString().slice(0, 5))
					setEndTime(endDate.toTimeString().slice(0, 5))
				}
			}
		}

		setIsModifyingShift(true)
	}

	const confirmModifyShift = async () => {
		if (!startTime || !endTime) return

		const newMonthlyShifts = {...monthlyShifts}
		const updatePromises = []

		selectedCells.forEach(cellId => {
			const [day, vehicleId, shiftType] = cellId.split("-")
			const dateKey = formatDate(currentYear, currentMonth, parseInt(day))
			const shift = newMonthlyShifts.calendar?.[dateKey]?.[vehicleId]?.[shiftType]

			if (shift && shift.shiftId) {
				const startDateTime = new Date(`${dateKey}T${startTime}:00`).toISOString()
				const endDateTime = new Date(`${dateKey}T${endTime}:00`).toISOString()

				// API call to update shift
				updatePromises.push(axios.put(`${SERVER_URI}/update_reserved_shift/`, {
					reservedShiftId: shift.shiftId,
					startTime:       startDateTime,
					endTime:         endDateTime,
					driverId:        shift.driverId,
					vehicleId:       vehicleId,
				}, {
					headers: {Authorization: `Token ${window.localStorage.getItem("admin_token")}`},
				}))

				// Update local state
				newMonthlyShifts.calendar[dateKey][vehicleId][shiftType] = {
					...shift, startTime: startDateTime, endTime: endDateTime,
				}
			}
		})

		try {
			await Promise.all(updatePromises)
			setMonthlyShifts(newMonthlyShifts)
			setSelectedCells(new Set())
			setIsModifyingShift(false)
			setStartTime("")
			setEndTime("")
		} catch (error) {
			console.error("Error updating shifts:", error)
			alert("Virhe vuorojen päivityksessä")
		}
	}

	const cancelModifyShift = () => {
		setIsModifyingShift(false)
		setStartTime("")
		setEndTime("")
	}

	const handleAddShift = () => {
		if (selectedCells.size === 0) return
		setIsAddingShift(true)
	}

	// Helper to extract "HH:mm" from ISO string
	function getTimeHHMM(isoString) {
		const date = new Date(isoString)
		const hours = String(date.getHours()).padStart(2, "0")
		const minutes = String(date.getMinutes()).padStart(2, "0")
		return `${hours}:${minutes}`
	}

	const confirmAddShift = async () => {
		if (!selectedDriver) return

		const newMonthlyShifts = {...monthlyShifts}
		const addPromises = []

		selectedCells.forEach(cellId => {
			const [day, vehicleId, shiftType] = cellId.split("-")
			const dateKey = formatDate(currentYear, currentMonth, parseInt(day))

			let start, end
			if (shiftType === "day") {
				if (!props.dayShiftTime?.dayShiftStart || !props.dayShiftTime?.dayShiftEnd) {
					console.error("Missing day shift start/end time in props.dayShiftTime")
					return
				}
				start = getTimeHHMM(props.dayShiftTime.dayShiftStart)
				end = getTimeHHMM(props.dayShiftTime.dayShiftEnd)
			} else {
				if (!props.nightShiftTime?.nightShiftStart || !props.nightShiftTime?.nightShiftEnd) {
					console.error("Missing night shift start/end time in props.nightShiftTime")
					return
				}
				start = getTimeHHMM(props.nightShiftTime.nightShiftStart)
				end = getTimeHHMM(props.nightShiftTime.nightShiftEnd)
			}

			if (!start || !end) {
				console.error("Invalid shift time:", {start, end, shiftType})
				return
			}

			const startDateTime = new Date(`${dateKey}T${start}:00`).toISOString()
			const endDateTime = new Date(`${dateKey}T${end}:00`).toISOString()
			console.log("Selected driver:", selectedDriver)

			addPromises.push(axios.post(`${SERVER_URI}/add_reserved_work_shift/`, {
				vehicleId:   parseInt(vehicleId),
				driverId:    parseInt(selectedDriver),
				shiftStarts: startDateTime,
				shiftEnds:   endDateTime,
			}, {
				headers: {Authorization: `Token ${window.localStorage.getItem("admin_token")}`},
			}).then(response => {
				if (!newMonthlyShifts.calendar) newMonthlyShifts.calendar = {}
				if (!newMonthlyShifts.calendar[dateKey]) newMonthlyShifts.calendar[dateKey] = {}
				if (!newMonthlyShifts.calendar[dateKey][vehicleId]) newMonthlyShifts.calendar[dateKey][vehicleId] = {}
				newMonthlyShifts.calendar[dateKey][vehicleId][shiftType] = response.data
			}))
		})

		try {
			await Promise.all(addPromises)
			setMonthlyShifts(newMonthlyShifts)
			setSelectedCells(new Set())
			setIsAddingShift(false)
			setSelectedDriver("")
		} catch (error) {
			console.error("Error adding shifts:", error)
			alert("Virhe vuorojen lisäyksessä")
		}
		// In confirmAddShift, after all API calls succeed:
		try {
			await Promise.all(addPromises)

			// Re-fetch the updated shifts from the server to ensure state is in sync
			const response = await axios.get(`${SERVER_URI}/list_monthly_reserved_shifts/`, {
				params:     {
					month: currentMonth + 1, year: currentYear,
				}, headers: {Authorization: `Token ${window.localStorage.getItem("admin_token")}`},
			})

			if (response.status === 200) {
				setMonthlyShifts(response.data)
			} else {
				console.error("Failed to fetch updated shifts:", response.statusText)
			}

			setSelectedCells(new Set())
			setIsAddingShift(false)
			setSelectedDriver("")
		} catch (error) {
			console.error("Error adding shifts:", error)
			alert("Virhe vuorojen lisäyksessä")
		}
	}

	const cancelAddShift = () => {
		setIsAddingShift(false)
		setSelectedDriver("")
	}

	function getSelectedData() {
		const selectedData = {}

		selectedCells.forEach(cellId => {
			const [year, month, day, vehicleId, shiftType] = cellId.split("-")
			const dateKey = formatDate(parseInt(year), parseInt(month) - 1, parseInt(day))
			const dayData = monthlyShifts.calendar?.[dateKey] || {}
			const shiftData = dayData[vehicleId]?.[shiftType]

			if (shiftData) {
				if (!selectedData[dateKey]) selectedData[dateKey] = {}
				if (!selectedData[dateKey][vehicleId]) selectedData[dateKey][vehicleId] = {}
				selectedData[dateKey][vehicleId][shiftType] = shiftData
			}
		})

		return selectedData
	}

	// Helper to check if all selected cells have shift data
	const areAllSelectedCellsFilled = () => {
		if (!monthlyShifts.calendar) return false
		for (const cellId of selectedCells) {
			const [year, month, day, vehicleId, shiftType] = cellId.split("-")
			const dateKey = formatDate(parseInt(year), parseInt(month) - 1, parseInt(day))
			const shiftData = monthlyShifts.calendar?.[dateKey]?.[vehicleId]?.[shiftType]
			if (!shiftData) return false
		}
		return selectedCells.size > 0
	}

	// Helper to check if all selected cells are empty
	const areAllSelectedCellsEmpty = () => {
		if (!monthlyShifts.calendar) return false
		for (const cellId of selectedCells) {
			const [year, month, day, vehicleId, shiftType] = cellId.split("-")
			const dateKey = formatDate(parseInt(year), parseInt(month) - 1, parseInt(day))
			const shiftData = monthlyShifts.calendar?.[dateKey]?.[vehicleId]?.[shiftType]
			if (shiftData) return false // Not empty
		}
		return selectedCells.size > 0
	}

	// Helper to get shiftData for a target cellId from copiedData
	function getShiftDataForCell(cellId) {
		const copiedCellIds = Array.from(copiedCells)

		// Single cell or block copy
		if (copiedCellIds.length === 1) {
			// Only one cell copied, use its data for all
			const copiedCellId = copiedCellIds[0]
			const [copyYear, copyMonth, copyDay, copyVehicleId, copyShiftType] = copiedCellId.split("-")
			const dateKey = formatDate(parseInt(copyYear), parseInt(copyMonth) - 1, parseInt(copyDay))
			return copiedData?.[dateKey]?.[copyVehicleId]?.[copyShiftType]
		}
		// Block copy: find relative position in copied block
		const copiedCellIndex = copiedCellIds.indexOf(cellId)
		if (copiedCellIndex !== -1) {
			const copiedCellId = copiedCellIds[copiedCellIndex]
			const [copyYear, copyMonth, copyDay, copyVehicleId, copyShiftType] = copiedCellId.split("-")
			const dateKey = formatDate(parseInt(copyYear), parseInt(copyMonth) - 1, parseInt(copyDay))
			return copiedData?.[dateKey]?.[copyVehicleId]?.[copyShiftType]
		}
		return null
	}

	/*
		const handlePaste = async () => {
			if (Object.keys(copiedData).length === 0 || selectedCells.size === 0) return;

			const addPromises = [];
			const vehicles = monthlyShifts.vehicles || [];

			const newMonthlyShifts = { ...monthlyShifts }

			// Check if only one date-vehicle combination was copied (but could have both shift types)
			const copiedCellIds = Array.from(copiedCells)
			const copiedDateVehicleCombos = [...new Set(copiedCellIds.map(id => {
				const [day, vehicleId] = id.split('-')
				return `${day}-${vehicleId}`
			}))]

			if (copiedDateVehicleCombos.length === 1) {
				// Single date-vehicle copied - fill all selected cells with the copied shift data
				const [copiedDay, copiedVehicleId] = copiedDateVehicleCombos[0].split('-')
				const copiedDateKey = formatDate(currentYear, currentMonth, parseInt(copiedDay))
				const copiedVehicleData = copiedData[copiedDateKey]?.[copiedVehicleId]

				if (copiedVehicleData) {
					selectedCells.forEach(cellId => {
						const [day, vehicleId, shiftType] = cellId.split('-')
						const dateKey = formatDate(currentYear, currentMonth, parseInt(day))

						if (!newMonthlyShifts.calendar) newMonthlyShifts.calendar = {}
						if (!newMonthlyShifts.calendar[dateKey]) newMonthlyShifts.calendar[dateKey] = {}
						if (!newMonthlyShifts.calendar[dateKey][vehicleId]) newMonthlyShifts.calendar[dateKey][vehicleId] = {}

						// Use the first available shift data from copied data for the target shift type
						const availableShiftTypes = Object.keys(copiedVehicleData)
						if (availableShiftTypes.length > 0) {
							const sourceShiftData = copiedVehicleData[availableShiftTypes[0]]
							newMonthlyShifts.calendar[dateKey][vehicleId][shiftType] = sourceShiftData
						}
					})
				}
			} else {
				// Multiple date-vehicle combinations copied - use existing relative positioning logic
				const firstSelectedCell = Array.from(selectedCells)[0]
				const [startDay, startVehicleId] = firstSelectedCell.split('-')

				const copiedDays = [...new Set(copiedCellIds.map(id => parseInt(id.split('-')[0])))].sort((a, b) => a - b)
				const copiedVehicles = [...new Set(copiedCellIds.map(id => id.split('-')[1]))]

				const dayOffset = parseInt(startDay) - copiedDays[0]
				const vehicleOffset = vehicles.indexOf(startVehicleId) - vehicles.indexOf(copiedVehicles[0])

				Object.keys(copiedData).forEach(dateKey => {
					const [day] = dateKey.split('-').map(Number)
					const newDay = day + dayOffset

					if (newDay >= 1 && newDay <= getDaysInMonth(currentYear, currentMonth)) {
						const newDateKey = formatDate(currentYear, currentMonth, newDay)

						Object.keys(copiedData[dateKey]).forEach(vehicleId => {
							const originalVehicleIndex = vehicles.indexOf(vehicleId)
							const newVehicleIndex = originalVehicleIndex + vehicleOffset

							if (newVehicleIndex >= 0 && newVehicleIndex < vehicles.length) {
								const newVehicleId = vehicles[newVehicleIndex]

								if (!newMonthlyShifts.calendar) newMonthlyShifts.calendar = {}
								if (!newMonthlyShifts.calendar[newDateKey]) newMonthlyShifts.calendar[newDateKey] = {}
								if (!newMonthlyShifts.calendar[newDateKey][newVehicleId]) newMonthlyShifts.calendar[newDateKey][newVehicleId] = {}

								Object.keys(copiedData[dateKey][vehicleId]).forEach(originalShiftType => {
									const shiftData = copiedData[dateKey][vehicleId][originalShiftType]
									const targetCellId = getCellId(newDay, newVehicleId, originalShiftType)

									if (selectedCells.has(targetCellId)) {
										newMonthlyShifts.calendar[newDateKey][newVehicleId][originalShiftType] = shiftData
									}
								})
							}
						})
					}
				})
			}
			// Make API calls to add the shifts
			// In handlePaste, when preparing the POST:
			// In handlePaste, when preparing the POST:
			selectedCells.forEach(cellId => {
				const [targetDay, targetVehicleId, targetShiftType] = cellId.split('-');
				const shiftData = getShiftDataForCell(cellId);

				if (shiftData && shiftData.driverId) {
					const dateKey = formatDate(currentYear, currentMonth, parseInt(targetDay));
					let start, end;

					if (targetShiftType === 'day') {
						start = getTimeHHMM(props.dayShiftTime.dayShiftStart);
						end = getTimeHHMM(props.dayShiftTime.dayShiftEnd);
					} else {
						start = getTimeHHMM(props.nightShiftTime.nightShiftStart);
						end = getTimeHHMM(props.nightShiftTime.nightShiftEnd);
					}

					addPromises.push(
						axios.post(`${SERVER_URI}/add_reserved_work_shift/`, {
							vehicleId: parseInt(targetVehicleId),
							driverId: parseInt(shiftData.driverId),
							shiftStarts: new Date(`${dateKey}T${start}:00`).toISOString(),
							shiftEnds: new Date(`${dateKey}T${end}:00`).toISOString()
						}, {
							headers: { Authorization: `Token ${window.localStorage.getItem("admin_token")}` }
						})
					);
				}
			});

			try {
				await Promise.all(addPromises);

				// Re-fetch the updated shifts from the server
				const response = await axios.get(`${SERVER_URI}/list_monthly_reserved_shifts/`, {
					params: {
						month: currentMonth + 1,
						year: currentYear,
					},
					headers: { Authorization: `Token ${window.localStorage.getItem("admin_token")}` }
				});

				if (response.status === 200) {
					setMonthlyShifts(response.data);
				} else {
					console.error("Failed to fetch updated shifts:", response.statusText);
				}

				setSelectedCells(new Set());
			} catch (error) {
				console.error("Error pasting shifts:", error);
				alert("Virhe vuorojen liittämisessä");
			}
		}
	*/

	const handlePaste = async () => {
		if (Object.keys(copiedData).length === 0 || selectedCells.size === 0) return

		let skipped = []
		const addPromises = []
		const vehiclesList = monthlyShifts.vehicles || []
		const shiftTypes = ["day", "night"]
		const copiedCellIds = Array.from(copiedCells)
		const selectedCellIds = Array.from(selectedCells)
		const copiedCellCount = copiedCells.size;
		const selectedCellCount = selectedCells.size;

		if (
			copiedCellCount > 1 &&
			selectedCellCount > 1 &&
			copiedCellCount !== selectedCellCount
		) {
			setPasteWarning(
				"Liitetään vain, jos valittuna on joko yksi tai yhtä monta solua kuin kopioituja soluja."
			);
			return;
		}
		setPasteWarning(""); // Clear warning if valid

		// Helper to check if a cell is empty
		const isCellEmpty = (dateKey, vehicleId, shiftType) => {
			return !monthlyShifts.calendar?.[dateKey]?.[vehicleId]?.[shiftType]
		}

		// 1-to-1 mapping for non-rectangular selection
		if (copiedCellIds.length === selectedCellIds.length) {
			for (let i = 0; i < selectedCellIds.length; i++) {
				const [targetYear, targetMonth, targetDay, targetVehicleId, targetShiftType] = selectedCellIds[i].split("-")
				const [sourceYear, sourceMonth, sourceDay, sourceVehicleId, sourceShiftType] = copiedCellIds[i].split("-")
				const dateKey = formatDate(parseInt(targetYear), parseInt(targetMonth) - 1, parseInt(targetDay))
				if (!isCellEmpty(dateKey, targetVehicleId, targetShiftType)) {
					skipped.push(selectedCellIds[i])
					console.log("Skipping non-empty target cell:", selectedCellIds[i])
					continue // Skip if target cell is not empty
				}

				const sourceDateKey = formatDate(parseInt(sourceYear), parseInt(sourceMonth) - 1, parseInt(sourceDay))
				const sourceShiftData = copiedData?.[sourceDateKey]?.[sourceVehicleId]?.[sourceShiftType]
				if (!sourceShiftData) continue

				let start, end
				if (targetShiftType === "day") {
					start = getTimeHHMM(props.dayShiftTime.dayShiftStart)
					end = getTimeHHMM(props.dayShiftTime.dayShiftEnd)
				} else {
					start = getTimeHHMM(props.nightShiftTime.nightShiftStart)
					end = getTimeHHMM(props.nightShiftTime.nightShiftEnd)
				}

				addPromises.push(axios.post(`${SERVER_URI}/add_reserved_work_shift/`, {
					vehicleId:   parseInt(targetVehicleId),
					driverId:    parseInt(sourceShiftData.driverId),
					shiftStarts: new Date(`${dateKey}T${start}:00`).toISOString(),
					shiftEnds:   new Date(`${dateKey}T${end}:00`).toISOString(),
				}, {
					headers: {Authorization: `Token ${window.localStorage.getItem("admin_token")}`},
				}))
			}
		} else if (selectedCells.size === 1 && copiedCellIds.length > 1) {
			// Block paste starting from a single selected cell, support month/year overflow
			const [targetCellId] = selectedCellIds
			const [targetYear, targetMonth, targetDay, targetVehicleId, targetShiftType] = targetCellId.split("-")

			// Parse copied cells into objects with full date
			const copiedParsed = copiedCellIds.map(id => {
				const [y, m, d, v, s] = id.split("-")
				return {
					id,
					year:      parseInt(y),
					month:     parseInt(m) - 1,
					day:       parseInt(d),
					vehicleId: v,
					shiftType: s,
					dateObj:   new Date(parseInt(y), parseInt(m) - 1, parseInt(d)),
				}
			})

			// Find the minimum copied date for offset calculation
			const minCopiedDateObj = copiedParsed.reduce((min, c) => c.dateObj < min
			                                                         ? c.dateObj
			                                                         : min, copiedParsed[0].dateObj)
			const minCopiedVehicleIndex = Math.min(...copiedParsed.map(c => vehiclesList.indexOf(c.vehicleId)))
			const minCopiedShiftTypeIndex = Math.min(...copiedParsed.map(c => shiftTypes.indexOf(c.shiftType)))

			for (const copied of copiedParsed) {
				const dayOffset = Math.round((copied.dateObj - minCopiedDateObj) / (1000 * 60 * 60 * 24))
				const vehicleOffset = vehiclesList.indexOf(copied.vehicleId) - minCopiedVehicleIndex
				const shiftTypeOffset = shiftTypes.indexOf(copied.shiftType) - minCopiedShiftTypeIndex

				// Use Date object to handle overflow
				const startDate = new Date(parseInt(targetYear), parseInt(targetMonth) - 1, parseInt(targetDay))
				startDate.setDate(startDate.getDate() + dayOffset)
				const pasteYear = startDate.getFullYear()
				const pasteMonth = startDate.getMonth()
				const pasteDay = startDate.getDate()

				const targetVehicleIndex = vehiclesList.indexOf(targetVehicleId) + vehicleOffset
				const targetShiftTypeIndex = shiftTypes.indexOf(targetShiftType) + shiftTypeOffset

				if (targetVehicleIndex < 0 || targetVehicleIndex >= vehiclesList.length || targetShiftTypeIndex < 0 || targetShiftTypeIndex >= shiftTypes.length) {
					continue // Out of bounds
				}

				const targetPasteVehicleId = vehiclesList[targetVehicleIndex]
				const targetPasteShiftType = shiftTypes[targetShiftTypeIndex]
				const dateKey = formatDate(pasteYear, pasteMonth, pasteDay)

				if (!isCellEmpty(dateKey, targetPasteVehicleId, targetPasteShiftType)) {
					// Push skipped cell into skipped array
					skipped.push(`${pasteYear}-${pasteMonth + 1}-${pasteDay}-${targetPasteVehicleId}-${targetPasteShiftType}`)
					console.log("Skipping non-empty target cell:", `${pasteYear}-${pasteMonth + 1}-${pasteDay}-${targetPasteVehicleId}-${targetPasteShiftType}`)
					continue
				}

				const sourceDateKey = formatDate(copied.year, copied.month, copied.day)
				const sourceShiftData = copiedData?.[sourceDateKey]?.[copied.vehicleId]?.[copied.shiftType]
				if (!sourceShiftData) continue

				let start, end
				if (targetPasteShiftType === "day") {
					start = getTimeHHMM(props.dayShiftTime.dayShiftStart)
					end = getTimeHHMM(props.dayShiftTime.dayShiftEnd)
				} else {
					start = getTimeHHMM(props.nightShiftTime.nightShiftStart)
					end = getTimeHHMM(props.nightShiftTime.nightShiftEnd)
				}

				addPromises.push(axios.post(`${SERVER_URI}/add_reserved_work_shift/`, {
					vehicleId:   parseInt(targetPasteVehicleId),
					driverId:    parseInt(sourceShiftData.driverId),
					shiftStarts: new Date(`${dateKey}T${start}:00`).toISOString(),
					shiftEnds:   new Date(`${dateKey}T${end}:00`).toISOString(),
				}, {
					headers: {Authorization: `Token ${window.localStorage.getItem("admin_token")}`},
				}))
			}
		} else {
			// Fallback: normal paste, but skip filled cells
			selectedCellIds.forEach(cellId => {
				const [targetYear, targetMonth, targetDay, targetVehicleId, targetShiftType] = cellId.split("-")
				const dateKey = formatDate(parseInt(targetYear), parseInt(targetMonth) - 1, parseInt(targetDay))
				if (!isCellEmpty(dateKey, targetVehicleId, targetShiftType)) {
					skipped.push(cellId)
					console.log("Skipping non-empty target cell:", cellId)
					return // Skip if target cell is not empty
				}

				const shiftData = getShiftDataForCell(cellId)

				if (shiftData && shiftData.driverId) {
					let start, end
					if (targetShiftType === "day") {
						start = getTimeHHMM(props.dayShiftTime.dayShiftStart)
						end = getTimeHHMM(props.dayShiftTime.dayShiftEnd)
					} else {
						start = getTimeHHMM(props.nightShiftTime.nightShiftStart)
						end = getTimeHHMM(props.nightShiftTime.nightShiftEnd)
					}

					addPromises.push(axios.post(`${SERVER_URI}/add_reserved_work_shift/`, {
						vehicleId:   parseInt(targetVehicleId),
						driverId:    parseInt(shiftData.driverId),
						shiftStarts: new Date(`${dateKey}T${start}:00`).toISOString(),
						shiftEnds:   new Date(`${dateKey}T${end}:00`).toISOString(),
					}, {
						headers: {Authorization: `Token ${window.localStorage.getItem("admin_token")}`},
					}))
				}
			})
		}

		try {
			await Promise.all(addPromises)
			const response = await axios.get(`${SERVER_URI}/list_monthly_reserved_shifts/`, {
				params:     {
					month: currentMonth + 1, year: currentYear,
				}, headers: {Authorization: `Token ${window.localStorage.getItem("admin_token")}`},
			})

			if (response.status === 200) {
				setMonthlyShifts(response.data)
			} else {
				console.error("Failed to fetch updated shifts:", response.statusText)
			}

			setSelectedCells(new Set())
		} catch (error) {
			console.error("Error pasting shifts:", error)
			alert("Virhe vuorojen liittämisessä")
		}
		setCopiedCells(new Set())
		setSkippedCells(skipped)
	}

	const handleCopy = () => {
		if (selectedCells.size > 0) {
			const dataToStore = getSelectedData()
			setCopiedCells(new Set(selectedCells))
			setCopiedData(dataToStore)
			console.log("Copied data:", dataToStore)
		}
	}

	const handleRemove = async () => {
		if (selectedCells.size === 0) return

		const confirmation = window.confirm("Haluatko varmasti poistaa valitut vuorot?")
		if (!confirmation) return

		const newMonthlyShifts = {...monthlyShifts}

		// Collect shiftIds and make API calls
		const deletePromises = []
		selectedCells.forEach(cellId => {
			const [year, month, day, vehicleId, shiftType] = cellId.split("-")
			const dateKey = formatDate(parseInt(year), parseInt(month) - 1, parseInt(day))
			const shift = newMonthlyShifts.calendar?.[dateKey]?.[vehicleId]?.[shiftType]

			if (shift && shift.shiftId) {
				// Make API call to delete shift
				deletePromises.push(axios.delete(`${SERVER_URI}/remove_reserved_work_shift/${shift.shiftId}`, {
					headers: {Authorization: `Token ${window.localStorage.getItem("admin_token")}`},
				}))
			}

			// Remove from local state
			if (shift) {
				delete newMonthlyShifts.calendar[dateKey][vehicleId][shiftType]
				if (Object.keys(newMonthlyShifts.calendar[dateKey][vehicleId]).length === 0) {
					delete newMonthlyShifts.calendar[dateKey][vehicleId]
				}
				if (Object.keys(newMonthlyShifts.calendar[dateKey]).length === 0) {
					delete newMonthlyShifts.calendar[dateKey]
				}
			}
		})

		// Wait for all API calls to finish
		try {
			await Promise.all(deletePromises)
			setMonthlyShifts(newMonthlyShifts)
			setSelectedCells(new Set())
		} catch (error) {
			console.error("Error deleting shifts:", error)
			alert("Virhe vuorojen poistossa")
		}
	}

	const getCellId = (year, month, day, vehicleId, shiftType) => `${year}-${month + 1}-${String(day)}-${vehicleId}-${shiftType}`

// Update handleCellMouseDown
	const handleCellMouseDown = (year, month, day, vehicleId, shiftType, event) => {
		event.preventDefault()
		const cellId = getCellId(year, month, day, vehicleId, shiftType)
		setSelectionStart({year, month, day, vehicleId, shiftType})
		setSelectedCells(new Set([cellId]))
		setIsSelecting(true)
	}

// Update handleCellMouseEnter
	// In handleCellMouseEnter, use the year/month from selectionStart and hovered cell
	const handleCellMouseEnter = (year, month, day, vehicleId, shiftType) => {
		if (!isSelecting || !selectionStart) return

		// Calculate date ranges using Date objects for cross-month selection
		const startDate = new Date(selectionStart.year, selectionStart.month, selectionStart.day)
		const endDate = new Date(year, month, day)

		const minDate = startDate < endDate ? startDate : endDate
		const maxDate = startDate > endDate ? startDate : endDate

		const startVehicleIndex = vehicles.indexOf(selectionStart.vehicleId)
		const endVehicleIndex = vehicles.indexOf(vehicleId)
		const minVehicleIndex = Math.min(startVehicleIndex, endVehicleIndex)
		const maxVehicleIndex = Math.max(startVehicleIndex, endVehicleIndex)

		let shiftTypesArr = []
		if (selectionStart.shiftType === shiftType) {
			shiftTypesArr = [shiftType]
		} else {
			shiftTypesArr = ["day", "night"]
		}

		const newSelection = new Set()
		let d = new Date(minDate)
		while (d <= maxDate) {
			for (let v = minVehicleIndex; v <= maxVehicleIndex; v++) {
				shiftTypesArr.forEach(type => {
					newSelection.add(getCellId(d.getFullYear(), d.getMonth(), d.getDate(), vehicles[v], type))
				})
			}
			d.setDate(d.getDate() + 1)
		}
		setSelectedCells(newSelection)
	}

	const handleMouseUp = useCallback(() => {
		setIsSelecting(false)
		console.log("Selected cells:", selectedCells)
	}, [setIsSelecting, selectedCells])

	// Add to ReservedShifts.js

	const autoScrollInterval = useRef(null)
	const SCROLL_EDGE_THRESHOLD = 50 // pixels from edge to trigger scroll
	const SCROLL_AMOUNT = 20 // pixels to scroll each interval

	const calendarGridRef = useRef(null)

	const stopAutoScroll = useCallback(() => {
		if (autoScrollInterval.current) {
			clearInterval(autoScrollInterval.current)
			autoScrollInterval.current = null
		}
	}, [])

	const startAutoScroll = useCallback((e) => {
		stopAutoScroll()
		autoScrollInterval.current = setInterval(() => {
			const grid = calendarGridRef.current
			if (!grid) return
			const rect = grid.getBoundingClientRect()
			const {clientX, clientY} = e

			if (clientY - rect.top < SCROLL_EDGE_THRESHOLD) {
				grid.scrollTop = Math.max(0, grid.scrollTop - SCROLL_AMOUNT)
			} else if (rect.bottom - clientY < SCROLL_EDGE_THRESHOLD) {
				grid.scrollTop = Math.min(grid.scrollHeight - grid.clientHeight, grid.scrollTop + SCROLL_AMOUNT)
			}
			if (clientX - rect.left < SCROLL_EDGE_THRESHOLD) {
				grid.scrollLeft = Math.max(0, grid.scrollLeft - SCROLL_AMOUNT)
			} else if (rect.right - clientX < SCROLL_EDGE_THRESHOLD) {
				grid.scrollLeft = Math.min(grid.scrollWidth - grid.clientWidth, grid.scrollLeft + SCROLL_AMOUNT)
			}
			// Optionally, update selection here if scrolled
		}, 50)
	}, [calendarGridRef, stopAutoScroll])

// Attach to grid mouse events
	useEffect(() => {
		function onMouseMove(e) {
			if (isSelecting) {
				startAutoScroll(e)
			} else {
				stopAutoScroll()
			}
		}

		function onMouseUp() {
			stopAutoScroll()
		}

		document.addEventListener("mousemove", onMouseMove)
		document.addEventListener("mouseup", onMouseUp)
		return () => {
			document.removeEventListener("mousemove", onMouseMove)
			document.removeEventListener("mouseup", onMouseUp)
			stopAutoScroll()
		}
	}, [isSelecting, startAutoScroll, stopAutoScroll])

	useEffect(() => {
		document.addEventListener("mouseup", handleMouseUp)
		return () => {
			document.removeEventListener("mouseup", handleMouseUp)
		}
	}, [handleMouseUp])

	useEffect(() => {
		const fetchCurrentMonthShifts = async () => {
			try {
				const response = await axios({
					method:     "GET", url: `${SERVER_URI}/list_monthly_reserved_shifts/`, params: {
						month: currentMonth + 1, // API expects month in 1-12 format
						year:  currentYear,
					}, headers: {Authorization: `Token ${window.localStorage.getItem("admin_token")}`},
				})
				console.log("Fetched monthly shifts:", response.data)
				if (response.status === 200) {
					setMonthlyShifts(response.data)
					setIsLoaded(true)
				} else {
					console.error("Failed to fetch monthly shifts:", response.statusText)
				}
			} catch (error) {
				console.error("Error fetching monthly shifts:", error)
				return
			}
		}
		fetchCurrentMonthShifts()
	}, [currentYear, currentMonth])

	if (!isLoaded) {
		return (<div className="loading">
			<h2>Ladataan vuoroja...</h2>
		</div>)
	}

	const formatDate = (year, month, day) => {
		return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
	}

	const getDayName = (year, month, day) => {
		const date = new Date(year, month, day)
		const days = ["Su", "Ma", "Ti", "Ke", "To", "Pe", "La"] // Finnish abbreviations
		return days[date.getDay()]
	}

	const navigateMonth = (direction) => {
		setIsLoaded(false)
		const newDate = new Date(currentDate)
		newDate.setMonth(currentDate.getMonth() + direction)
		setCurrentDate(newDate)
	}

	const vehicles = monthlyShifts.vehicles || []

	// Helper to get the first visible (Monday) and last visible (Sunday) dates for the grid
	const getVisibleDateRange = (year, month) => {
		const firstOfMonth = new Date(year, month, 1)
		const lastOfMonth = new Date(year, month + 1, 0)

		// Monday before or on the first day
		const start = new Date(firstOfMonth)
		const dayOfWeekStart = start.getDay() === 0 ? 7 : start.getDay() // 1=Mon, 7=Sun
		start.setDate(start.getDate() - (dayOfWeekStart - 1))

		// Sunday after or on the last day
		const end = new Date(lastOfMonth)
		const dayOfWeekEnd = end.getDay() === 0 ? 7 : end.getDay()
		end.setDate(end.getDate() + (7 - dayOfWeekEnd))

		return {start, end}
	}

	const {start: gridStart, end: gridEnd} = getVisibleDateRange(currentYear, currentMonth)

// Calculate all days to render
	const daysToRender = []
	let d = new Date(gridStart)
	while (d <= gridEnd) {
		daysToRender.push(new Date(d))
		d.setDate(d.getDate() + 1)
	}

	return (<div className="reserved-shifts">
		{/* Month Navigation */}
		<div className="month-navigation">
			<button onClick={() => navigateMonth(-1)}>← Edellinen</button>
			<h2>{currentMonth + 1}/{currentYear}</h2>
			<button onClick={() => navigateMonth(1)}>Seuraava →</button>
		</div>
		<div className="toolbar">
			<button
				onClick={handleCopy}
				disabled={selectedCells.size === 0}
				className="copy-button"
			>
				Kopioi ({selectedCells.size})
			</button>
			<button
				onClick={handlePaste}
				disabled={Object.keys(copiedData).length === 0 || selectedCells.size === 0}
				className="paste-button"
			>
				Liitä
			</button>
			<button
				onClick={handleRemove}
				disabled={selectedCells.size === 0}
				className="remove-button"
			>
				Poista ({selectedCells.size})
			</button>
			<button
				onClick={handleAddShift}
				disabled={selectedCells.size === 0 || !areAllSelectedCellsEmpty()}
				className="add-button"
			>
				Lisää vuoro ({selectedCells.size})
			</button>
			<button
				onClick={handleModifyShift}
				disabled={selectedCells.size === 0 || !areAllSelectedCellsFilled()}
				className="modify-button"
			>
				Muokkaa ({selectedCells.size})
			</button>
		</div>
		{pasteWarning && (
			<div className="paste-warning">
				{pasteWarning}
				<button onClick={() => setPasteWarning("")}>Ok</button>
			</div>
		)}
		{/* Calendar Grid */}
		<div className="calendar-grid">
			{/* Header Row */}
				{skippedCells.length > 0 && <span className="skipped-info">
						<div>
							{`Huomioi, että ${skippedCells.length} valittua vuoroa on jo varattu.`}
							<button onClick={() => setSkippedCells([])} className="clear-skipped-button">
								Ok
							</button>
						</div>
					</span>}
			<div className="calendar-scroll-container" ref={calendarGridRef}>
				<div className="calendar-header">
					<div className="date-column">Päivä</div>
					{vehicles.map(vehicleId => (<div key={vehicleId} className="vehicle-column">
						<div className="vehicle-header">Ajoneuvo {vehicleId}</div>
						<div className="shift-headers">
							<div className="day-shift-header">Päivä</div>
							<div className="night-shift-header">Yö</div>
						</div>
					</div>))}
				</div>

				{/* Calendar Body */}
				<div className="calendar-body">
					{daysToRender.map(dateObj => {
						const year = dateObj.getFullYear()
						const month = dateObj.getMonth()
						const day = dateObj.getDate()
						const dateKey = formatDate(year, month, day)
						const dayData = monthlyShifts.calendar?.[dateKey] || {}
						const dayName = getDayName(year, month, day)
						const isCurrentDate = currentYear === year && currentMonth === month && day === (new Date()).getDate()
						const isOtherMonth = month !== currentMonth

						return (<div key={dateKey} className={`calendar-row${isOtherMonth ? " other-month" : ""}`}>
							<div className={`date-cell ${isCurrentDate ? "current-date" : ""}`}>
								<div className="date-number">{day}</div>
								<div className="day-name">{dayName}</div>
							</div>
							{vehicles.map(vehicleId => (<div key={vehicleId} className="vehicle-shifts">
								<div
									className={`day-shift ${selectedCells.has(getCellId(year, month, day, vehicleId, "day"))
									                        ? "selected"
									                        : ""} ${copiedCells.has(getCellId(year, month, day, vehicleId, "day"))
									                                ? "copied"
									                                : ""}
											                                 ${skippedCells.includes(getCellId(year, month, day, vehicleId, "day"))
									                                       ? "skipped"
									                                       : ""}
											                                `}
									onMouseDown={(e) => handleCellMouseDown(year, month, day, vehicleId, "day", e)}
									onMouseEnter={() => handleCellMouseEnter(year, month, day, vehicleId, "day")}
								>
									{dayData[vehicleId]?.day?.driverName || ""}
								</div>
								<div
									className={`night-shift ${selectedCells.has(getCellId(year, month, day, vehicleId, "night"))
									                          ? "selected"
									                          : ""} ${copiedCells.has(getCellId(year, month, day, vehicleId, "night"))
									                                  ? "copied"
									                                  : ""}
											                                  ${skippedCells.includes(getCellId(year, month, day, vehicleId, "night"))
									                                        ? "skipped"
									                                        : ""}
											                                  `}
									onMouseDown={(e) => handleCellMouseDown(year, month, day, vehicleId, "night", e)}
									onMouseEnter={() => handleCellMouseEnter(year, month, day, vehicleId, "night")}
								>
									{dayData[vehicleId]?.night?.driverName || ""}
								</div>
							</div>))}
						</div>)
					})}
				</div>
			</div>
		</div>
		{isAddingShift && (<div className="modal-overlay">
			<div className="modal">
				<h3>Lisää vuoro</h3>
				<div className="form-group">
					<label>Kuljettaja:</label>
					<select
						value={selectedDriver}
						onChange={(e) => setSelectedDriver(e.target.value)}
					>
						<option value="">Valitse kuljettaja</option>
						{driverList.map(driver => (<option key={driver.driverId} value={driver.driverId}>
							{driver.name}
						</option>))}
					</select>
				</div>
				<div className="modal-buttons">
					<button onClick={confirmAddShift} disabled={!selectedDriver}>
						Lisää
					</button>
					<button onClick={cancelAddShift}>
						Peruuta
					</button>
				</div>
			</div>
		</div>)}
		{isModifyingShift && (<div className="modal-overlay">
			<div className="modal">
				<h3>Muokkaa vuoroja</h3>
				<div className="form-group">
					<label>Alkuaika:</label>
					<input
						type="time"
						value={startTime}
						onChange={(e) => setStartTime(e.target.value)}
					/>
				</div>
				<div className="form-group">
					<label>Loppuaika:</label>
					<input
						type="time"
						value={endTime}
						onChange={(e) => setEndTime(e.target.value)}
					/>
				</div>
				<div className="modal-buttons">
					<button onClick={confirmModifyShift} disabled={!startTime || !endTime}>
						Tallenna
					</button>
					<button onClick={cancelModifyShift}>
						Peruuta
					</button>
				</div>
			</div>
		</div>)}
	</div>)
}

